import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export interface DashboardReading {
  timestamp: string;
  value: number;
  unit: string;
  maxParticleAcceleration?: number;
  accelerationExceededAlert?: boolean;
  accelerationExceededAlarm?: boolean;
  accelerationExceededAction?: boolean;
  operationInProgress?: string;
  weatherCondition?: string;
  displacementX?: number;
  displacementY?: number;
  displacementZ?: number;
  /** raw crack X reading (mm) — backend mislabels as "Change" */
  crackWidthX?: number;
  /** raw crack Y reading (mm) — backend mislabels as "Change" */
  crackWidthY?: number;
  crackWidthXChange?: number;
  crackWidthYChange?: number;
  inclinationReading?: number;
  inclinationChange?: number;
  inclinationRatio?: string;
  /** movement: raw settlement reading (mPD) */
  movementSettlement?: number;
  /** movement: raw northing reading (m) */
  movementNorthing?: number;
  /** movement: raw easting reading (m) */
  movementEasting?: number;
  /** movement: lateral displacement from first reading (mm) */
  movementDistance?: number;
}

export interface DashboardDevice {
  deviceId: string;
  deviceName: string;
  deviceType: string;
  readings: DashboardReading[];
  location: { northing: number; easting: number };
}

export interface DashboardData {
  devices: DashboardDevice[];
  lastUpdated: string;
}

export interface DashboardEarliestDateResponse {
  earliestDate: string | null;
}

export interface ProjectInfo {
  id: number;
  name: string;
  layout_image_filename: string | null;
}

export interface ActivityDeviceSummary {
  id: string;
  device_name: string;
  device_type: string;
}

export interface Activity {
  id: number;
  project_id: number;
  activity_name: string;
  from_date: string;
  to_date: string;
  notes: string | null;
  devices: ActivityDeviceSummary[];
}

export interface AlertLevel {
  order: number;
  name: string;
  value: number;
  entry_mode?: "value" | "gradient";
}

export type AlertLevelJson = AlertLevel[] | { [group: string]: AlertLevel[] };

export interface AlertProfile {
  id: number;
  project_id: number | null;
  description: string;
  profile_type: string;
  alert_level_json: AlertLevelJson;
}

export interface DistortionTimeSeriesPoint {
  date: string;
  distortion: number;
  distortionRatio: string | null;
  differentialSettlement: number;
  readingA: number;
  readingB: number;
}

export interface DistortionTimeSeriesData {
  pairId: string;
  pairLabel: string | null;
  pointAName: string;
  pointBName: string;
  baselineDistanceM: number;
  alertProfileId: number | null;
  alertProfileDescription: string | null;
  data: DistortionTimeSeriesPoint[];
}

export interface DistortionPairRecord {
  id: string;
  projectId: number;
  pairLabel: string | null;
  pointAName?: string;
  pointBName?: string;
  pointAType?: string;
  pointBType?: string;
  alertProfileId?: number | null;
  baselineDistanceM: number;
}

export const dashboardApi = createApi({
  reducerPath: "dashboardApi",
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL }),
  tagTypes: ["Dashboard"],
  endpoints: (builder) => ({
    getDashboard: builder.query<
      DashboardData,
      {
        projectId?: number;
        date?: string;
        startDate?: string;
        endDate?: string;
        deviceNames?: string[];
      }
    >({
      query: (params) => {
        const sp = new URLSearchParams();
        if (params.projectId != null)
          sp.append("projectId", String(params.projectId));
        if (params.date) sp.append("date", params.date);

        // Add date range if provided (Dashboard component manages defaults)
        if (params.startDate) {
          sp.append("startDate", params.startDate);
        }
        if (params.endDate) {
          sp.append("endDate", params.endDate);
        }

        if (params.deviceNames && params.deviceNames.length > 0) {
          sp.append("deviceNames", params.deviceNames.join(","));
        }

        return { url: `daily-readings/dashboard?${sp.toString()}` };
      },
      providesTags: ["Dashboard"],
    }),
    getDashboardEarliestDate: builder.query<
      DashboardEarliestDateResponse,
      { projectId?: number; deviceType: string }
    >({
      query: ({ projectId, deviceType }) => {
        const sp = new URLSearchParams({ deviceType });
        if (projectId != null) sp.append("projectId", String(projectId));
        return {
          url: `daily-readings/dashboard-earliest-date?${sp.toString()}`,
        };
      },
    }),
    getProjects: builder.query<ProjectInfo[], void>({
      query: () => "projects",
    }),
    getPiezometerContour: builder.query<
      any,
      { projectId: number; date?: string }
    >({
      query: ({ projectId, date }) => {
        const sp = new URLSearchParams({ projectId: String(projectId) });
        if (date) sp.append("date", date);
        return { url: `daily-readings/piezometer-contour?${sp.toString()}` };
      },
    }),
    getStandpipeData: builder.query<any, { projectId: number; date?: string }>({
      query: ({ projectId, date }) => {
        const sp = new URLSearchParams({ projectId: String(projectId) });
        if (date) sp.append("date", date);
        return { url: `daily-readings/standpipe-data?${sp.toString()}` };
      },
    }),
    getBuildingSettlementContour: builder.query<
      any,
      { projectId: number; date?: string }
    >({
      query: ({ projectId, date }) => {
        const sp = new URLSearchParams({ projectId: String(projectId) });
        if (date) sp.append("date", date);
        return {
          url: `daily-readings/building-settlement-contour?${sp.toString()}`,
        };
      },
    }),
    getGroundSettlementContour: builder.query<
      any,
      { projectId: number; date?: string }
    >({
      query: ({ projectId, date }) => {
        const sp = new URLSearchParams({ projectId: String(projectId) });
        if (date) sp.append("date", date);
        return {
          url: `daily-readings/ground-settlement-contour?${sp.toString()}`,
        };
      },
    }),
    getUtilitySettlementContour: builder.query<
      any,
      { projectId: number; date?: string }
    >({
      query: ({ projectId, date }) => {
        const sp = new URLSearchParams({ projectId: String(projectId) });
        if (date) sp.append("date", date);
        return {
          url: `daily-readings/utility-settlement-contour?${sp.toString()}`,
        };
      },
    }),
    getMovementSettlementContour: builder.query<
      any,
      { projectId: number; date?: string; deviceType?: string }
    >({
      query: ({ projectId, date, deviceType }) => {
        const sp = new URLSearchParams({ projectId: String(projectId) });
        if (date) sp.append("date", date);
        if (deviceType) sp.append("deviceType", deviceType);
        return {
          url: `daily-readings/movement-settlement-contour?${sp.toString()}`,
        };
      },
    }),
    getActivities: builder.query<
      Activity[],
      { projectId: number; from?: string; to?: string }
    >({
      query: ({ projectId, from, to }) => {
        const sp = new URLSearchParams({ project_id: String(projectId) });
        if (from) sp.append("from", from);
        if (to) sp.append("to", to);
        return { url: `activities?${sp.toString()}` };
      },
      transformResponse: (res: any): Activity[] =>
        Array.isArray(res) ? res : (res?.data ?? []),
    }),
    getAlertProfiles: builder.query<
      AlertProfile[],
      { projectId: number; profileType?: string }
    >({
      query: ({ projectId, profileType }) => {
        const sp = new URLSearchParams({ project_id: String(projectId) });
        if (profileType) sp.append("profile_type", profileType);
        return { url: `alert-profiles?${sp.toString()}` };
      },
    }),
    getDistortionPairs: builder.query<
      DistortionPairRecord[],
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `distortion-pairs?projectId=${projectId}`,
      }),
      transformResponse: (res: any): DistortionPairRecord[] =>
        Array.isArray(res) ? res : (res?.data ?? []),
    }),
    getDistortionTimeSeries: builder.query<
      DistortionTimeSeriesData[],
      { projectId: number }
    >({
      query: ({ projectId }) => ({
        url: `distortion-pairs/time-series?projectId=${projectId}`,
      }),
      transformResponse: (res: any): DistortionTimeSeriesData[] =>
        Array.isArray(res) ? res : (res?.data ?? []),
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetDashboardEarliestDateQuery,
  useGetProjectsQuery,
  useGetPiezometerContourQuery,
  useGetStandpipeDataQuery,
  useGetBuildingSettlementContourQuery,
  useGetGroundSettlementContourQuery,
  useGetUtilitySettlementContourQuery,
  useGetMovementSettlementContourQuery,
  useGetActivitiesQuery,
  useGetAlertProfilesQuery,
  useGetDistortionPairsQuery,
  useGetDistortionTimeSeriesQuery,
} = dashboardApi;
